import { useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { sessionId, trackPartner } from "@/lib/partners";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Check, ImagePlus, Loader2, X } from "lucide-react";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.22em] text-gray-400">{label}</span>
    {children}
  </label>
);

const line = "mt-2 h-12 rounded-none bg-transparent border-0 border-b border-gray-200 text-base px-0 focus-visible:ring-0 focus-visible:border-[#c19a6b]";

export const LeadForm = ({ slug, realtorName }) => {
  const [f, setF] = useState({
    first_name: "", last_name: "", email: "", phone: "", city: "", moving: false, move_date: "",
    pieces: "", notes: "",
  });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [started, setStarted] = useState(false);
  const fileRef = useRef();

  const set = (k, v) => {
    if (!started) {
      setStarted(true);
      trackPartner(slug, "quote_start");
    }
    setF((x) => ({ ...x, [k]: v }));
  };

  const submit = async () => {
    if (!f.first_name.trim() || !f.email.trim()) {
      toast.error("Please add your name and email so we can send your quote");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/revival/${slug}/lead`, {
        ...f, session_id: sessionId(), entry_url: window.location.href,
      });
      if (photos.length) {
        const fd = new FormData();
        photos.forEach((p) => fd.append("files", p));
        await axios.post(`${API}/public/leads/${data.upload_token}/photos`, fd).catch(() => {});
      }
      setDone(true);
      window.scrollTo({ top: document.getElementById("request")?.offsetTop - 40, behavior: "smooth" });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "We couldn't send your request. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto text-center fade-up" data-testid="lead-success">
        <div className="w-12 h-12 border border-[#c19a6b] rounded-full flex items-center justify-center mx-auto mb-8">
          <Check size={20} className="text-[#c19a6b]" />
        </div>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Thank you, {f.first_name}.</h2>
        <p className="text-gray-600 mt-6 leading-relaxed">
          Your request is with ARK. We'll review your pieces and send your personalized revival quote — with your{" "}
          {realtorName} client rate already applied.
        </p>
        <div className="bg-[#FAF7F2] border-l-2 border-[#c19a6b] p-7 mt-10 text-left">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#c19a6b] font-bold">What happens next</p>
          <ol className="mt-5 space-y-3 text-sm text-gray-600 leading-relaxed">
            <li>1. ARK reviews your pieces and confirms resurfacing is the right service.</li>
            <li>2. You receive your private client quote — Signature Revival at the Essential rate.</li>
            <li>3. You choose your option and reserve, and we coordinate pickup and delivery.</li>
          </ol>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="lead-form">
      <div className="space-y-9">
        <div className="grid sm:grid-cols-2 gap-9">
          <Field label="First name *"><Input data-testid="lead-first-name" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} className={line} /></Field>
          <Field label="Last name"><Input data-testid="lead-last-name" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} className={line} /></Field>
          <Field label="Email *"><Input data-testid="lead-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className={line} /></Field>
          <Field label="Phone"><Input data-testid="lead-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} className={line} /></Field>
          <Field label="City"><Input data-testid="lead-city" value={f.city} onChange={(e) => set("city", e.target.value)} className={line} /></Field>
          <Field label="Move date (if you're moving)"><Input data-testid="lead-move-date" placeholder="October 12, 2026" value={f.move_date} onChange={(e) => set("move_date", e.target.value)} className={line} /></Field>
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox data-testid="lead-moving" checked={f.moving} onCheckedChange={(v) => set("moving", !!v)} className="mt-0.5 data-[state=checked]:bg-[#c19a6b] data-[state=checked]:border-[#c19a6b]" />
          <span className="text-sm text-gray-600 leading-snug">
            I'm moving — I'd like ARK to pick up from my current home and deliver to my new home.
          </span>
        </label>

        <Field label="What would you like revived?">
          <Textarea
            data-testid="lead-pieces"
            rows={3}
            placeholder="Oak dining table, 6 chairs and a sideboard"
            value={f.pieces}
            onChange={(e) => set("pieces", e.target.value)}
            className="mt-2 rounded-none bg-transparent border-0 border-b border-gray-200 px-0 text-base focus-visible:ring-0 focus-visible:border-[#c19a6b] resize-none"
          />
        </Field>

        <Field label="Anything else we should know?">
          <Textarea
            data-testid="lead-notes"
            rows={2}
            value={f.notes}
            onChange={(e) => set("notes", e.target.value)}
            className="mt-2 rounded-none bg-transparent border-0 border-b border-gray-200 px-0 text-base focus-visible:ring-0 focus-visible:border-[#c19a6b] resize-none"
          />
        </Field>

        <div>
          <span className="text-[10px] uppercase tracking-[0.22em] text-gray-400">Photos (optional, up to 10)</span>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => setPhotos((p) => [...p, ...Array.from(e.target.files || [])].slice(0, 10))} />
          <div className="flex flex-wrap gap-3 mt-4">
            {photos.map((p, i) => (
              <div key={i} className="relative w-20 h-20">
                <img src={URL.createObjectURL(p)} alt={p.name} className="w-full h-full object-cover" />
                <button
                  data-testid={`remove-photo-${i}`}
                  onClick={() => setPhotos((ps) => ps.filter((_, x) => x !== i))}
                  className="absolute -top-2 -right-2 bg-black text-white p-1"
                >
                  <X size={11} />
                </button>
              </div>
            ))}
            <button
              data-testid="lead-add-photos"
              onClick={() => fileRef.current.click()}
              className="w-20 h-20 border border-dashed border-gray-300 hover:border-[#c19a6b] flex items-center justify-center text-gray-400 hover:text-[#c19a6b] transition-colors duration-200"
            >
              <ImagePlus size={18} />
            </button>
          </div>
        </div>
      </div>

      <button
        data-testid="lead-submit-btn"
        onClick={submit}
        disabled={busy}
        className="w-full mt-12 h-14 bg-black text-white text-xs uppercase tracking-[0.25em] font-bold btn-lift disabled:opacity-60 inline-flex items-center justify-center gap-2"
      >
        {busy && <Loader2 size={15} className="animate-spin" />} Request My Private Client Quote
      </button>
      <p className="text-center text-[11px] text-gray-400 mt-5 tracking-wide">
        No obligation. Your {realtorName} client rate is applied automatically.
      </p>
    </div>
  );
};
